/* eslint-disable curly */
import Route from './route';
import EmberObject from '@ember/object';
import { getOwner } from '@ember/application';
import { inject as service } from '@ember/service';
import { equal } from '@ember/object/computed';
import { isMeWeTeam } from 'mewe/helpers/is-mewe-team';
import { debounce } from 'lodash';
import EmojiUtils from 'mewe/utils/emoji-utils';
import { Theme, ChatFilter } from 'mewe/constants';
import AccountApi from 'mewe/api/account-api';
import PopupsApi from 'mewe/api/popups-api';
import TranslationApi from 'mewe/api/translation-api';
import { ds } from 'mewe/stores/ds';
import { fetchFollowed } from 'mewe/fetchers/fetch-pages';
import fetchThreads from 'mewe/fetchers/fetch-threads';
import fetchFavorites from 'mewe/fetchers/fetch-favorites';
import fetchNotificationsCount from 'mewe/fetchers/fetch-notifications-count';
import fetchGroups from 'mewe/fetchers/fetch-groups';
import GroupStore from 'mewe/stores/group-store';
import NotificationsStore from 'mewe/stores/notifications-store';
import CurrentUserStore from 'mewe/stores/current-user-store';
import ChatStore from 'mewe/stores/chat-store';
import PurchasesStore from 'mewe/stores/purchases-store';
import Storage from 'mewe/shared/storage';
import { isInJail, isLocked, isDsnpUserWithoutName } from 'mewe/utils/jail-utils';
import { isSafari } from 'mewe/shared/utils';
import PS from 'mewe/utils/pubsub';
import { readChatFilter } from 'mewe/utils/chat-utils';
import Session from 'mewe/shared/session';
import * as Sentry from '@sentry/ember';
import Verbose from 'mewe/utils/verbose';
import { loadDarkTheme } from 'mewe/utils/miscellaneous-utils';
import EnvironmentUtils from 'mewe/utils/environment-utils';

const verbose = Verbose({ prefix: '[App Route]' }).log;

export default Route.extend({
  purchase: service(),
  settings: service(),
  account: service(),
  features: service(),
  websocket: service(),
  analytics: service(),
  dynamicDialogs: service(),
  router: service(),

  beforeModel() {
    Session.isAuthenticated().then(({ isAuthenticated }) => {
      if (isAuthenticated) fetchThreads();
      CurrentUserStore.send('handle', { isAuthenticated });
    });
  },

  model(params) {
    this.queryParams = params;

    return EmberObject.extend({
      theme: this.theme || '',

      isMyWorld: equal('theme', Theme.CONTACTS),
      isGroup: equal('theme', Theme.GROUP),
      isMyCloud: equal('theme', Theme.MYCLOUD),
      isChat: equal('theme', Theme.CHAT),
      isEvents: equal('theme', Theme.EVENTS),
      isEvent: equal('theme', Theme.EVENT),
      isAllGroups: equal('theme', Theme.GROUPS),
      isAllPages: equal('theme', Theme.PAGES),

      groupState: GroupStore.getState(),
      notifications: NotificationsStore.getState(),
      upcomingEvents: ds.events.for('upcoming-events'),
      followedPages: ds.followed,
      storeState: PurchasesStore.getState(),
      chats: ChatStore.getState(),
    }).create();
  },

  async init() {
    this._super();

    if (location.pathname.indexOf('/500') === 0) {
      return;
    }

    loadDarkTheme();

    try {
      const { isAuthenticated, isConfirmed, isLocked } = await Session.isAuthenticated();
      if (isAuthenticated && isConfirmed) {
        if (this.useCampaings) {
          const noCampaigns = await this.checkForCampaigns();

          if (noCampaigns) {
            this.runApp(isLocked);
          }
        } else {
          this.runApp(isLocked);
        }

        if (~this.getHash().indexOf('exportContent')) {
          this.dynamicDialogs.openDialog('download-content-dialog');
        }
      }
      this.analytics.sendEvent('isUsingDarkTheme', { isEnabled: this.settings.isDarkThemeEnabled, onLoad: true });
    } catch (e) {}
  },

  runApp(isTokenLocked) {
    fetchGroups();
    fetchFollowed();
    fetchNotificationsCount();
    fetchFavorites();
    this.account.loadFeatureFlags();
    // if name or something did change, we get it from this request
    AccountApi.getCurrentUser().then((data) => {
      CurrentUserStore.send('handle', data, true);

      let user = CurrentUserStore.getState();

      Sentry.configureScope((scope) => {
        scope.setUser({
          id: user.id,
          username: user.name,
          email: user.primaryEmail,
        });
      });

      Sentry.setTag('product', 'consumer');
      Sentry.setTag('locale', user.locale);

      if (data.needTosAccepted) {
        this.dispatch('app', 'showTosDialog');
      }

      if (isDsnpUserWithoutName(user)) {
        if (!~window.location.pathname.indexOf('/account/dsnp/siwa/signin')) {
          window.location.href = '/account/dsnp/siwa/signin';
        }
      } else if (isInJail(user)) {
        if (!~window.location.pathname.indexOf('/lockout')) {
          window.location.href = '/lockout';
        }
      } else if (isLocked(user)) {
        if (!~window.location.pathname.indexOf('/verify')) {
          window.location.href = '/verify';
        }
      } else if (isTokenLocked) {
        // lock info in tokens not yet up-to-date with /info
        AccountApi.updateLockStatus().finally(() => {
          window.location.reload();
        });
      } else {
        const filter = readChatFilter();

        if (filter && filter !== ChatFilter.ALL) {
          fetchThreads({ chatType: filter });
        }

        TranslationApi.getDefault().then((data) => {
          if (data) {
            CurrentUserStore.send('handle', {
              supportedLanguages: data.supportedLanguages,
              minimumNumberOfCharacters: data.minimumNumberOfCharacters,
              defaultLanguage: data.defaultLocale,
            });
          }
        });

        // dsnpHandle not created yet, await WS message with confirmation
        if (!user.dsnpHandle) {
          PS.Sub('msa.created', (data) => {
            CurrentUserStore.send('handle', {
              msaId: data.msaId,
              dsnpHandle: data.handle,
              publicLinkId: data.handle,
            });
          });
        }

        if (~this.getHash().indexOf('migrated')) {
          this.dynamicDialogs.openDialog('simple-dialog-new', {
            closeBtnText: __(`Exit`),
            message: __(
              `Congratulations! You have already migrated your account to web3.<br><br>There is nothing else you need to do.`
            ),
            noEscaping: true,
          });
        }

        this.getGlobalAutoPlaySetting();
      }
    });

    EmojiUtils.preparePurchasedHashMaps(this.purchase);

    // comment should persist only transitions between routes, not page reload so clean it on page load
    Storage.remove(Storage.keys.composedCommentsData);

    if (window.Worker) {
      this.runActivityChecker(); // in case of working with site after longer inactivity (waking laptop from sleep) - reconnecting to WS
    }

    this.websocket.open();

    if (isSafari()) {
      this.unlockAudio();
    }

    this.initDebugDialog();

    // send darkTheme analytics only for logged in users (doens't make sense for public visitors)
    this.analytics.sendEvent('isUsingDarkTheme', { isEnabled: this.settings.isDarkThemeEnabled, onLoad: true });
  },

  useCampaings: !~window.location.pathname?.indexOf('delete-account'),

  // hack, if user will trigger sound on click then safari not block sounds triggered without user's interaction
  // https://stackoverflow.com/questions/54047606/allow-audio-play-on-safari-for-chat-app
  unlockAudio() {
    function unlock() {
      const sound = new Audio('/assets/audio/blank.mp3'); // 1ms of silence
      sound.play();

      document.body.removeEventListener('click', unlock);
      document.body.removeEventListener('touchstart', unlock);
    }

    document.body.addEventListener('click', unlock);
    document.body.addEventListener('touchstart', unlock);
  },

  getGlobalAutoPlaySetting() {
    AccountApi.getPreferences().then(({ autoPlayVideos, allowNsfwContent }) => {
      Storage.set(Storage.keys.videoFeedAutoPlaySetting, autoPlayVideos);
      Storage.set(Storage.keys.allowNsfwContentSetting, allowNsfwContent);
    });
  },

  initDebugDialog() {
    if (isMeWeTeam() || localStorage.getItem('showDebugDialog') === 'true') {
      const debugListener = (event) => {
        if (event.shiftKey && event.altKey && event.code === 'KeyD') {
          this.dynamicDialogs.openDialog('debug-dialog');
        }
      };
      document.addEventListener('keydown', debugListener);
      // Store listener reference for cleanup
      this._debugListener = debugListener;
    }
  },

  async checkForCampaigns() {
    if (!this.features.isEnabled('userCampaigns')) return true;

    try {
      const { popUps } = await PopupsApi.getPopups();
      if (!popUps || !popUps.length) return true; //When no popups, run the app

      popUps.forEach(popup => {
        // if there is no host then it's internal link, adding our host to it
        // otherwise it can be opened as relative path to e.g. group URL (MW-878)
        const learnMoreUrl = EnvironmentUtils.processRedirectionUrl(popup.learnMore?.path);
        const ctaUrl = EnvironmentUtils.processRedirectionUrl(popup.cta?.url);

        const params = {
          title: popup.title,
          message: popup.subtitle,
          imagePath: popup.imagePath,
          isCloseBlocked: popup.closeable === false,
          blurApp: popup.closeable === false,
          classes: 'generic-popup',
          classImg: 'box-shadow-none',
          learnMoreText: popup.learnMore?.text,
          learnMoreLink: learnMoreUrl,
          okButtonText: popup.cta?.text,
          onClose: () => {
            this.runApp();
            PopupsApi.mark(popup.id, 'closed');
          },
          onConfirm: () => {
            // when we dont want to close the dialog after opening the link we do redirect in same window
            if (popup.closeable === false) {
              PopupsApi.mark(popup.id, 'completed')
                .then(() => {
                  window.location.href = ctaUrl;
                });
            } else {
              window.open(ctaUrl);
              PopupsApi.mark(popup.id, 'completed');
            }
          },
        };

        PopupsApi.mark(popup.id, 'viewed');
        this.dynamicDialogs.openDialog('simple-dialog-new', params);
      });
    } catch (e) {
      return true;
    }
  },

  /*
   * To reconnect to WS in case of long inactivity (waking up from sleep - laptop, #SG-8081)
   */
  runActivityChecker() {
    // https://mmazzarolo.com/blog/2021-09-03-loading-web-workers-using-webpack-5/
    const wakeupWorker = new Worker(new URL('../workers/detect-wakeup-worker.js', import.meta.url));
    const self = this;
    const oncePerMilliSeconds = 3000;

    window.addEventListener(
      'online',
      debounce(() => {
        verbose(`client is online: ${new Date()}`);
        self.refresh();
      }, oncePerMilliSeconds),
      false
    );

    wakeupWorker.onmessage = (ev) => {
      if (ev && ev.data === 'wakeup') {
        verbose(`client just woke up: ${new Date()}`);
        self.refresh();
      }
    };
  },

  refresh() {
    this._super();

    verbose(`updating data ${new Date()}`);

    this.websocket.restart();

    this.websocket.deferred.promise.then(() => {
      fetchGroups();
      fetchThreads({ selectLastThread: true, reconnectRefresh: true });
    });
  },

  activate() {
    const { body } = getOwner(this).lookup('service:-document');
    const html = body.parentNode;
    html.classList.add('app');
  },

  deactivate() {
    const { body } = getOwner(this).lookup('service:-document');
    const html = body.parentNode;
    html.classList.remove('app');

    if (this._debugListener) {
      document.removeEventListener('keydown', this._debugListener);
      this._debugListener = null;
    }
  },

  getHash() {
    return window.location.hash || this.currentModel?.locationHash || '';
  },
});
